<div class="h-100 d-flex flex-column p-4">
    <dx-load-panel
        container=".view-wrapper-scroll"
        [visible]="isLoading"
        [showPane]="false"
    >
    </dx-load-panel>

    @if (order$ | async; as order) {
        <dx-scroll-view class="view-wrapper-scroll" [style]="(`height: calc(100vh - ${offset}px)`)">
            <div class="form-container">
                @if (canEdit) {
                    <div class="card border-0 shadow-sm mb-3 py-1 px-4">
                        <dx-toolbar class="theme-dependent">
                            <dxi-item location="before">
                                <div class="fs-5 fw-medium">Edit PO</div>
                            </dxi-item>
                            <dxi-item location="after" locateInMenu="never">
                                <dx-button
                                    text="Cancel"
                                    stylingMode="outlined"
                                    type="normal"
                                    [disabled]="!isDataChanged || isSubmitting"
                                    (onClick)="cancel()"
                                ></dx-button>
                            </dxi-item>
                            <dxi-item location="after" locateInMenu="never">
                                <dx-button
                                    text="Save"
                                    icon="save"
                                    stylingMode="contained"
                                    type="default"
                                    [disabled]="!isDataChanged || isSubmitting"
                                    (onClick)="save()"
                                ></dx-button>
                            </dxi-item>
                        </dx-toolbar>
                    </div>
                }
                <div class="card border-0 shadow-sm pt-0 pb-1 mb-4">
                    <div class="card-header fs-5 fw-medium d-flex justify-content-between align-items-center bg-transparent py-3">
                        <div>Order Details</div>
                        <div class="text-muted">
                            ID: {{ order.salesDocument }}
                        </div>
                    </div>
                    <div class="card-body">
                        <!-- Order Overview -->
                        <div class="text-uppercase text-muted small fw-semibold mb-3 mx-3">Order Overview</div>
                        <div class="row g-3 mx-3">
                            <div class="col-md-4">
                                <div class="mb-2">
                                    <span class="fw-bold me-2">Pre-Production Status:</span>
                                    <span class="badge" [ngClass]="getPreProductionBadgeClass(order.preProductionStatus)">
                                        {{ order.preProductionStatus | preProductionStatus }}
                                    </span>
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold">Sales Document:</span> {{ order.salesDocument }}
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold">Document Date:</span> {{ order.documentDate | date }}
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold">Logistical Group:</span> {{ order.logisticalGroup }}
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold">Sales Organization:</span> {{ order.salesOrganization }}
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="mb-2">
                                    <span class="fw-bold me-2">Exception:</span>
                                    @if (order.exception) {
                                        <span class="badge bg-danger-subtle text-danger">{{ order.exception }}</span>
                                    }
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold me-2">Missed Opportunity:</span>
                                    <span class="d-inline-flex flex-wrap gap-1 align-middle">
                                        @for (opportunity of order.missedOpportunities; track opportunity) {
                                            <span class="badge bg-warning-subtle text-warning">{{ opportunity | missedOpportunity }}</span>
                                        }
                                    </span>
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold">Case Pick:</span> {{ order.casePick }}
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold">Layer Pick:</span> {{ order.layerPick }}
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold">Pallet Pick:</span> {{ order.palletPick }}
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="mb-2">
                                    <span class="fw-bold me-2">Production Status:</span>
                                    <span class="badge" [ngClass]="getProductionBadgeClass(order.productionStatus)">
                                        {{ order.productionStatus | productionStatus }}
                                    </span>
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold">Overall WM Status:</span> {{ getStatusText(order.overallWmStatus) }}
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold">Staging Area:</span> {{ order.stagingArea }}
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold">Loading:</span> {{ order.loading | date: dateTimeFormat }}
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold me-2">Empty Trailer:</span>
                                    @if (order.emptyTrailerNumber) {
                                        <a [routerLink]="`/inbound/empty-check-ins/details/${order.emptyTrailerNumber}`" class="text-decoration-none">{{ order.emptyTrailerNumber }}</a>
                                    }
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold me-2">Outbound:</span>
                                    @if (order.outboundNumber) {
                                        <a [routerLink]="`/outbound/empty-check-outs/details/${order.outboundNumber}`" class="text-decoration-none">{{ order.outboundNumber }}</a>
                                    }
                                </div>
                            </div>
                        </div>

                        <hr class="my-4 text-muted opacity-25">

                        <!-- Appointment Details -->
                        <div class="text-uppercase text-muted small fw-semibold mb-3 mx-3">Appointment Details</div>
                        <div class="row g-3 mx-3">
                            <div class="col-md-4">
                                <div class="mb-2">
                                    <span class="fw-bold">Delivery Number:</span> {{ order.deliveryNumber }}
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold">Delivery Created:</span> {{ order.deliveryCreated | date: dateTimeFormat }}
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold me-2">Appointment Status:</span>
                                    @if (order.appointmentStatus) {
                                        <span class="badge" [ngClass]="getAppointmentStatusBadgeClass(order.appointmentStatus)">
                                            {{ order.appointmentStatus | orderAppointmentStatus }}
                                        </span>
                                    }
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold">Appointment:</span>
                                    @if (order.appointmentStatus === AppointmentStatus.LtlLoad) {
                                        N/A
                                    } @else if (order.appointment) {
                                        @if (appointmentHasTime(order)) {
                                            {{ order.appointment | easternTime }} ET
                                        } @else {
                                            {{ order.appointment | date }}
                                        }
                                    }
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="mb-2">
                                    <span class="fw-bold">MDT:</span>
                                    @if (order.appointmentStatus === AppointmentStatus.LtlLoad) {
                                        N/A
                                    } @else if (order.mdt) {
                                        {{ order.mdt | easternTime }} ET
                                    } @else if (order.appointment) {
                                        No Match
                                    }
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold">Ship to ID:</span> {{ order.shipToId }}
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold">Ship to Name:</span> {{ order.shipToName }}
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold">Ship to Street Number:</span> {{ order.shipToStreetNumber }}
                                </div>
                            </div>
                            <div class="col-md-4">
                                <div class="mb-2">
                                    <span class="fw-bold">Ship to Street:</span> {{ order.shipToStreet }}
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold">Ship to City:</span> {{ order.shipToCity }}
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold">Ship to Region:</span> {{ order.shipToRegion }}
                                </div>
                                <div class="mb-2">
                                    <span class="fw-bold">Ship to Postal Code:</span> {{ order.shipToPostalCode }}
                                </div>
                            </div>
                        </div>

                        @if (canViewSchedulingReferences) {
                            <hr class="my-4 text-muted opacity-25">

                            <div class="text-uppercase text-muted small fw-semibold mb-3 mx-3">Scheduling References</div>
                            <div class="row g-3 mx-3">
                                <div class="col-md-4">
                                    <div class="mb-2">
                                        <span class="fw-bold">Requested Delivery Date (OTR):</span> {{ order.requestedDeliveryDate | date }}
                                    </div>
                                    <div class="mb-2">
                                        <span class="fw-bold">Delivery Appointment (OTR):</span> {{ order.deliveryAppointment | date: dateTimeFormat }}
                                    </div>
                                    <div class="mb-2">
                                        <span class="fw-bold">Delivery Date/Time (VL06):</span> {{ order.deliveryDateTime | date: dateTimeFormat }}
                                    </div>
                                </div>
                                <div class="col-md-4">
                                    <div class="mb-2">
                                        <span class="fw-bold">Source Location Appointment Date (TM):</span>
                                        @if (order.sourceLocationAppointmentDate) {
                                            {{ order.sourceLocationAppointmentDate | easternTime }} ET
                                        }
                                    </div>
                                    <div class="mb-2">
                                        <span class="fw-bold">Destination Location Appointment Date (TM):</span>
                                        @if (order.destinationLocationAppointmentDate) {
                                            {{ order.destinationLocationAppointmentDate | date: dateTimeFormat : 'UTC' }} — {{ order.destinationLocationAppointmentTimeZone }}
                                        }
                                    </div>
                                </div>
                                <div class="col-md-4">
                                    @if (canEdit) {
                                    <dx-validation-group #overrideValidation>
                                        <div class="fw-bold mb-2">Appointment Override:</div>
                                        <div class="form-check">
                                            <input
                                                type="radio"
                                                class="form-check-input"
                                                id="override-ltl"
                                                [checked]="editOverride === Override.Ltl"
                                                [disabled]="!canEdit"
                                                (click)="selectOption(Override.Ltl)"
                                            >
                                            <label class="form-check-label" for="override-ltl">
                                                Mark as LTL load, appointment not required
                                            </label>
                                        </div>
                                        <div class="form-check">
                                            <input
                                                type="radio"
                                                class="form-check-input"
                                                id="override-manual"
                                                [checked]="editOverride === Override.Manual"
                                                [disabled]="!canEdit"
                                                (click)="selectOption(Override.Manual)"
                                            >
                                            <label class="form-check-label" for="override-manual">
                                                Set a custom appointment date and time
                                            </label>
                                        </div>
                                        @if (editOverride === Override.Manual) {
                                            <div class="mt-2">
                                                <dx-date-box
                                                    label="Appointment Date & Time"
                                                    stylingMode="filled"
                                                    type="datetime"
                                                    [displayFormat]="dateTimeFormat"
                                                    [(value)]="editAppointment"
                                                    [disabled]="!canEdit"
                                                >
                                                    <dx-validator>
                                                        <dxi-validation-rule type="required" message="Please select an appointment date and time."></dxi-validation-rule>
                                                    </dx-validator>
                                                </dx-date-box>
                                            </div>
                                        }
                                    </dx-validation-group>
                                    }
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>
            <dx-data-grid
                id="orderLinesGrid"
                class="grid theme-dependent"
                noDataText=""
                [dataSource]="order.lines"
                [allowColumnResizing]="true"
                [allowColumnReordering]="true"
                [showBorders]="true"
                [columnAutoWidth]="true"
                [wordWrapEnabled]="false"
                [stateStoring]="{ enabled: true, type: 'localStorage', storageKey: 'order-lines-grid-state', savingTimeout: 0 }"
                (onExporting)="onExporting($event)"
                [customizeColumns]="customizeColumns"
                [keyExpr]="['salesDocumentItem', 'productionItem']"
                width="100%"
            >
                <dxo-scrolling [scrollByContent]="true"></dxo-scrolling>
                <dxo-paging [pageSize]="5"></dxo-paging>
                <dxo-pager [visible]="true" [displayMode]="'full'" [showPageSizeSelector]="true" [showInfo]="true" [showNavigationButtons]="true"></dxo-pager>
                <dxo-load-panel [showPane]="false"></dxo-load-panel>
                <dxo-sorting mode="multiple"></dxo-sorting>
                <dxo-header-filter [visible]="true"></dxo-header-filter>
                <dxo-filter-row [visible]="true" [applyFilter]="'auto'"></dxo-filter-row>
                <dxo-search-panel [visible]="true" placeholder="Search"></dxo-search-panel>
                <dxo-export [enabled]="true" [formats]="['xlsx']"></dxo-export>
                <dxo-column-chooser height="340px" [enabled]="true" [mode]="'select'">
                    <dxo-position my="right top" at="right bottom" of=".dx-datagrid-column-chooser-button"></dxo-position>
                    <dxo-search [enabled]="true"></dxo-search>
                    <dxo-selection [allowSelectAll]="true" [selectByClick]="true" [recursive]="true"></dxo-selection>
                </dxo-column-chooser>

                <dxo-toolbar>
                    <dxi-item location="before"><div class="grid-header">Lines</div></dxi-item>
                    <dxi-item location="after" locateInMenu="auto" showText="inMenu" widget="dxButton" [options]="{ text: 'Clear Filters', icon: 'fa-sharp fa-thin fa-filter-slash', onClick: clearFilters, stylingMode: 'text' }"></dxi-item>
                    <dxi-item location="after" locateInMenu="auto" showText="inMenu" widget="dxButton" [options]="{ text: 'Reset', icon: 'restore', onClick: resetGrid, stylingMode: 'text' }"></dxi-item>
                    <dxi-item location="after" locateInMenu="auto"><div class="separator"></div></dxi-item>
                    <dxi-item name="columnChooserButton" locateInMenu="auto"></dxi-item>
                    <dxi-item name="exportButton"></dxi-item>
                </dxo-toolbar>

                <dxi-column [dataField]="gridFieldName('salesDocumentItem')" caption="Item" [minWidth]="100" [visible]="true" [sortIndex]="0" sortOrder="asc"></dxi-column>
                <dxi-column [dataField]="gridFieldName('productionItem')" caption="Production Item" [minWidth]="120" [visible]="false" [sortIndex]="1" sortOrder="asc"></dxi-column>
                <dxi-column [dataField]="gridFieldName('material')" caption="Material" [minWidth]="120" [visible]="true"></dxi-column>
                <dxi-column [dataField]="gridFieldName('materialDescription')" caption="Material Description" [minWidth]="180" [visible]="true"></dxi-column>
                <dxi-column [dataField]="gridFieldName('batch')" caption="Batch" [minWidth]="120" [visible]="true"></dxi-column>
                <dxi-column [dataField]="gridFieldName('orderQuantity')" caption="Order Quantity" dataType="number" [minWidth]="120" [visible]="true"></dxi-column>
                <dxi-column [dataField]="gridFieldName('orderConfirmedQuantity')" caption="Order Confirmed Quantity" dataType="number" [minWidth]="140" [visible]="true"></dxi-column>
                <dxi-column [dataField]="gridFieldName('actualQuantityDelivered')" caption="Actual Quantity Delivered" dataType="number" [minWidth]="140" [visible]="true"></dxi-column>
                <dxi-column [dataField]="gridFieldName('deliveryQuantity')" caption="Delivery Quantity" dataType="number" [minWidth]="140" [visible]="true"></dxi-column>
                <dxi-column [dataField]="gridFieldName('reasonForRejection')" caption="Reason For Rejection" [minWidth]="150" [visible]="true"></dxi-column>
                <dxi-column [dataField]="gridFieldName('poNumber')" caption="PO Number" [minWidth]="120" [visible]="true"></dxi-column>
                <dxi-column [dataField]="gridFieldName('salesOrganization')" caption="Sales Organization" [minWidth]="150" [visible]="true"></dxi-column>
                <dxi-column [dataField]="gridFieldName('logisticalGroup')" caption="Logistical Group" [minWidth]="150" [visible]="true"></dxi-column>
                <dxi-column [dataField]="gridFieldName('deliveryNumber')" caption="Delivery Number" [minWidth]="150" [visible]="true"></dxi-column>
                <dxi-column [dataField]="gridFieldName('wmActivityStatus')" caption="WM Activity Status" [lookup]="{ dataSource: statusOptions, valueExpr: 'value', displayExpr: 'text' }" [minWidth]="150" [visible]="true"></dxi-column>
                <dxi-column [dataField]="gridFieldName('overallPickStatus')" caption="Overall Pick Status" [lookup]="{ dataSource: statusOptions, valueExpr: 'value', displayExpr: 'text' }" [minWidth]="150" [visible]="false"></dxi-column>
                <dxi-column [dataField]="gridFieldName('preProductionStatus')" caption="Pre Production Status" [lookup]="{ dataSource: preProductionStatusOptions, valueExpr: 'value', displayExpr: 'text' }" [minWidth]="150" [visible]="true" cellTemplate="statusTemplate"></dxi-column>
                <dxi-column [dataField]="gridFieldName('exceptions')" caption="Exception" [minWidth]="180" [visible]="true" [calculateCellValue]="calculateExceptionsCellValue" [allowSorting]="true" [allowFiltering]="true" cellTemplate="exceptionTemplate"></dxi-column>
                <dxi-column [dataField]="gridFieldName('missedOpportunities')" caption="Missed Opportunity" [minWidth]="180" [visible]="true" [calculateCellValue]="calculateMissedOpportunityCellValue" [allowSorting]="true" [allowFiltering]="true" cellTemplate="missedOpportunityTemplate"></dxi-column>
                <dxi-column [dataField]="gridFieldName('casesPerLayer')" caption="Cases Per Layer" dataType="number" [minWidth]="130" [visible]="true"></dxi-column>
                <dxi-column [dataField]="gridFieldName('casesPerPallet')" caption="Cases Per Pallet" dataType="number" [minWidth]="130" [visible]="true"></dxi-column>
                <dxi-column [dataField]="gridFieldName('casePick')" caption="Case Pick" dataType="number" [minWidth]="110" [visible]="true"></dxi-column>
                <dxi-column [dataField]="gridFieldName('layerPick')" caption="Layer Pick" dataType="number" [minWidth]="110" [visible]="true"></dxi-column>
                <dxi-column [dataField]="gridFieldName('palletPick')" caption="Pallet Pick" dataType="number" [minWidth]="110" [visible]="true"></dxi-column>

                <dxi-column [dataField]="gridFieldName('corporateBrand')" caption="Corporate Brand" [minWidth]="150" [visible]="false"></dxi-column>
                <dxi-column [dataField]="gridFieldName('corporateBrandDescription')" caption="Corporate Brand Description" [minWidth]="180" [visible]="false"></dxi-column>
                <dxi-column [dataField]="gridFieldName('rangeBrand')" caption="Range Brand" [minWidth]="150" [visible]="false"></dxi-column>
                <dxi-column [dataField]="gridFieldName('rangeBrandDescription')" caption="Range Brand Description" [minWidth]="180" [visible]="false"></dxi-column>
                <dxi-column [dataField]="gridFieldName('brandDenomination')" caption="Brand Denomination" [minWidth]="150" [visible]="false"></dxi-column>
                <dxi-column [dataField]="gridFieldName('brandDenominationD')" caption="Brand Denomination D" [minWidth]="150" [visible]="false"></dxi-column>
                <dxi-column [dataField]="gridFieldName('deliveryCreated')" caption="Delivery Created" dataType="datetime" [minWidth]="170" [visible]="false" cellTemplate="deliveryCreatedTemplate"></dxi-column>
                <dxi-column [dataField]="gridFieldName('releasedToHdcAt')" caption="Released to HDC Date & Time" dataType="datetime" [minWidth]="200" [visible]="false" cellTemplate="releasedToHdcAtTemplate"></dxi-column>

                <div *dxTemplate="let cellInfo of 'deliveryCreatedTemplate'">{{ getCellData(cellInfo).deliveryCreated | date: dateTimeFormat }}</div>
                <div *dxTemplate="let cellInfo of 'releasedToHdcAtTemplate'">{{ getCellData(cellInfo).releasedToHdcAt | date: dateTimeFormat }}</div>

                <div *dxTemplate="let cellInfo of 'statusTemplate'">
                    @if (cellInfo.value) {
                        <span class="badge" [ngClass]="getPreProductionBadgeClass(cellInfo.value)">
                            {{ cellInfo.value | preProductionStatus }}
                        </span>
                    }
                </div>

                <div *dxTemplate="let cellInfo of 'exceptionTemplate'">
                    <div class="d-flex flex-column align-items-start gap-1">
                        @for (exception of cellInfo.data.exceptions; track exception) {
                            <span class="badge bg-danger-subtle text-danger">{{ exception | orderException }}</span>
                        }
                    </div>
                </div>

                <div *dxTemplate="let cellInfo of 'missedOpportunityTemplate'">
                    <div class="d-flex flex-column align-items-start gap-1">
                        @for (opportunity of cellInfo.data.missedOpportunities; track opportunity) {
                            <span class="badge bg-warning-subtle text-warning">{{ opportunity | missedOpportunity }}</span>
                        }
                    </div>
                </div>

                <dxo-summary>
                    <dxi-total-item [column]="gridFieldName('orderQuantity')" summaryType="sum" [customizeText]="customizeSum"></dxi-total-item>
                    <dxi-total-item [column]="gridFieldName('orderConfirmedQuantity')" summaryType="sum" [customizeText]="customizeSum"></dxi-total-item>
                    <dxi-total-item [column]="gridFieldName('actualQuantityDelivered')" summaryType="sum" [customizeText]="customizeSum"></dxi-total-item>
                    <dxi-total-item [column]="gridFieldName('deliveryQuantity')" summaryType="sum" [customizeText]="customizeSum"></dxi-total-item>
                    <dxi-total-item [column]="gridFieldName('casePick')" summaryType="sum" [customizeText]="customizeSum"></dxi-total-item>
                    <dxi-total-item [column]="gridFieldName('layerPick')" summaryType="sum" [customizeText]="customizeSum"></dxi-total-item>
                    <dxi-total-item [column]="gridFieldName('palletPick')" summaryType="sum" [customizeText]="customizeSum"></dxi-total-item>
                </dxo-summary>
            </dx-data-grid>
        </dx-scroll-view>
    }
</div>
